export const DEFAULT_PRIMARY_COLOR = "#B98022";
export const SECONDARY_COLOR = "#131B2A";
export const SECONDARY_HOVER_COLOR = "#0F1624";
export const SECONDARY_FOREGROUND = "#FFFFFF";

export const BLACK = "#000000";
export const WHITE = "#FFFFFF";

export type ThemeColorAnalysis = {
  color: string | null;
  isValid: boolean;
  isAppropriate: boolean;
  preferredForeground: typeof BLACK | typeof WHITE;
  contrastWithBlack: number;
  contrastWithWhite: number;
  suggestedColor: string | null;
};

const normalizeHexColor = (color: string): string | null => {
  const candidate = color.trim();
  const shortMatch = /^#([\da-f]{3})$/i.exec(candidate);
  if (shortMatch) {
    const [red, green, blue] = shortMatch[1].split("");
    return `#${red}${red}${green}${green}${blue}${blue}`.toUpperCase();
  }
  return /^#[\da-f]{6}$/i.test(candidate) ? candidate.toUpperCase() : null;
};

const hexToRgb = (color: string): [number, number, number] => [
  Number.parseInt(color.slice(1, 3), 16),
  Number.parseInt(color.slice(3, 5), 16),
  Number.parseInt(color.slice(5, 7), 16),
];

const rgbToHex = ([red, green, blue]: [number, number, number]): string =>
  `#${[red, green, blue].map((channel) => Math.round(channel).toString(16).padStart(2, "0")).join("")}`.toUpperCase();

/** Calculates WCAG relative luminance for a hexadecimal sRGB color. */
export const getRelativeLuminance = (color: string): number | null => {
  const normalized = normalizeHexColor(color);
  if (!normalized) return null;
  const [red, green, blue] = hexToRgb(normalized).map((channel) => {
    const value = channel / 255;
    return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
};

/** Calculates the WCAG contrast ratio between two hexadecimal sRGB colors. */
export const getContrastRatio = (firstColor: string, secondColor: string): number | null => {
  const firstLuminance = getRelativeLuminance(firstColor);
  const secondLuminance = getRelativeLuminance(secondColor);
  if (firstLuminance === null || secondLuminance === null) return null;
  const lighter = Math.max(firstLuminance, secondLuminance);
  const darker = Math.min(firstLuminance, secondLuminance);
  return (lighter + 0.05) / (darker + 0.05);
};

const findAccessibleSurfaceColor = (color: string): string => {
  const rgb = hexToRgb(color);
  for (let step = 0; step <= 100; step += 1) {
    const ratio = 1 - step / 100;
    const candidate = rgbToHex(rgb.map((channel) => channel * ratio) as [number, number, number]);
    const surfaceContrast = getContrastRatio(candidate, WHITE) || 0;
    const foregroundContrast = Math.max(
      getContrastRatio(candidate, BLACK) || 0,
      getContrastRatio(candidate, WHITE) || 0,
    );
    if (surfaceContrast >= 3 && foregroundContrast >= 4.5) return candidate;
  }
  return DEFAULT_PRIMARY_COLOR;
};

/**
 * Evaluates a primary color against black and white and suggests the nearest darker
 * alternative when it cannot define a visible control boundary on a white surface.
 */
export const analyzeThemeColor = (color: string): ThemeColorAnalysis => {
  const normalized = normalizeHexColor(color);
  if (!normalized) {
    return {
      color: null,
      isValid: false,
      isAppropriate: false,
      preferredForeground: BLACK,
      contrastWithBlack: 0,
      contrastWithWhite: 0,
      suggestedColor: DEFAULT_PRIMARY_COLOR,
    };
  }

  const contrastWithBlack = getContrastRatio(normalized, BLACK) || 0;
  const contrastWithWhite = getContrastRatio(normalized, WHITE) || 0;
  const preferredForeground = contrastWithBlack >= contrastWithWhite ? BLACK : WHITE;
  const isAppropriate = contrastWithWhite >= 3 && Math.max(contrastWithBlack, contrastWithWhite) >= 4.5;

  return {
    color: normalized,
    isValid: true,
    isAppropriate,
    preferredForeground,
    contrastWithBlack,
    contrastWithWhite,
    suggestedColor: isAppropriate ? null : findAccessibleSurfaceColor(normalized),
  };
};

export type ResolvedSecondaryColor = {
  color: string;
  hover: string;
  active: string;
  isCustom: boolean;
};

const darkenColor = (color: string, amount: number): string =>
  rgbToHex(hexToRgb(color).map((channel) => channel * (1 - amount)) as [number, number, number]);

/**
 * Resolves the configurable secondary color used for solid buttons and dark panels.
 * Falls back to `fallback` (the Tripbox navy by default) when the value is missing, invalid,
 * or too light for white text.
 */
export const resolveSecondaryColor = (color?: string | null, fallback: string = SECONDARY_COLOR): ResolvedSecondaryColor => {
  const normalized = color ? normalizeHexColor(color) : null;
  const isUsable = normalized !== null && (getContrastRatio(normalized, SECONDARY_FOREGROUND) || 0) >= 4.5;
  const resolved = isUsable ? normalized : normalizeHexColor(fallback) || SECONDARY_COLOR;
  return {
    color: resolved,
    hover: !isUsable && resolved === SECONDARY_COLOR ? SECONDARY_HOVER_COLOR : darkenColor(resolved, 0.18),
    active: darkenColor(resolved, 0.32),
    isCustom: isUsable,
  };
};
